import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { map, tap } from 'rxjs/operators';
import { forkJoin, Observable } from 'rxjs';

import { environment } from 'src/environments/environment';

import { HEADER_DONT_SAVE, HEADER_DONT_SAVE_REQ, HEADER_RESET_REQ } from '@app/constants';
import { Developer, DevReqFileType } from '@interfaces/app';
import { AcceptedFundsQueryResponse } from '@app/interfaces';
import { UserWalletLiquidationMove } from './developer.interface';

@Injectable({
   providedIn: 'root'
})
export class DeveloperService {
   private readonly cacheDeveloper: { [prop: string]: {} } = {};

   actualRequest: string;

   resetDeveloperInfo = false;

   private readonly banxicoToken = 'b0c4f9402f06255e8812460d07877eecef230021654bc651e471bd6e64dda61f';

   constructor(private readonly http: HttpClient) { }

   getDeveloper(resetReq = true) {
      return this.http.post<Developer>(`${environment.apiUrlBase}/developer`, {},
         this.resetDeveloperInfo || resetReq ? HEADER_DONT_SAVE : {}).pipe(tap(() => this.resetDeveloperInfo = true));
   }

   updateRepresentativeInfo(data: any) {
      return this.http.post<{}>(`${environment.apiUrlBase}/developer/update/rep-info`, data, HEADER_DONT_SAVE);
   }

   createReqEvaluationDevelopment(data: any) {
      return this.http.post<{ id: string; irr: number; name: string }>(`${environment.apiUrlBase}/developer/dev/valuation`, data, HEADER_DONT_SAVE)
         .pipe(tap((r) => { this.actualRequest = r.id; this.resetDeveloperInfo = true; }));
   }

   updateRequestDevelopment(data: any) {
      return this.http.post<{ id: string }>(`${environment.apiUrlBase}/developer/dev/request`, data, HEADER_DONT_SAVE)
         .pipe(tap((r) => { this.actualRequest = r.id; this.resetDeveloperInfo = true; }));
   }

   updateRequestUnitsDevelopment(units: any[]) {
      return this.http.post<{ id: string }>(`${environment.apiUrlBase}/developer/dev/request-units`,
         { reqId: this.actualRequest, units }, HEADER_DONT_SAVE).pipe(
            tap((r) => { this.actualRequest = r.id; this.resetDeveloperInfo = true; }));
   }

   updateRequestInOutDevelopment(inOut: any) {
      return this.http.post<{ id: string }>(`${environment.apiUrlBase}/developer/dev/request-in-out`,
         { reqId: this.actualRequest || '6f3e99fb-9860-4e51-998c-9cedada84f80', inOut }, HEADER_DONT_SAVE).pipe(
            tap((r) => { this.actualRequest = r.id; this.resetDeveloperInfo = true; }));
   }

   uploadReqFile(file: File, idRequest: string, type: DevReqFileType) {
      const data = new FormData();
      const params = btoa(type);

      data.set('file', file);
      data.set('p', params);
      data.set('di', idRequest);

      return this.http.post<{ url: string }>(`${environment.apiUrlBase}/developer/dev/request/fl`, data,
         { headers: HEADER_DONT_SAVE.headers.set('tk', idRequest) });
   }

   uploadFile(file: File, type: 'cert' | 'act' | 'power' | 'ine') {
      const data = new FormData();
      const params = btoa(type);

      data.set('file', file);
      data.set('p', params);

      return this.http.post<{ url: string }>(`${environment.apiUrlBase}/developer/upload/fl`, data, HEADER_DONT_SAVE)
         .pipe(tap(() => this.resetDeveloperInfo = true));
   }

   /** VIDEO VALIDACION Y FORMULARIO DE CONTACTO PRIMERA VEZ */
   sendValidateVideo(name: string, phone: string, message: string, viewVideo: Boolean, email: string) {
      return this.http.post<any[]>(`${environment.apiUrlBase}/developer/set-validate-video`, { name, phone, message, viewVideo, email }, HEADER_DONT_SAVE)
   }

   /** DOC https://www.banxico.org.mx/SieAPIRest/service/v1/doc/catalogoSeries# */
   getActualUDIValue() {
      return this.http.get<{ bmx: { series: [{ idSerie: "SP68257", titulo: "Valor de UDIS", datos: [{ fecha: "10/01/2022", dato: "7.112667" }] }] } }>
         ('https://www.banxico.org.mx/SieAPIRest/service/v1/series/SP68257/datos/oportuno',
            { params: { token: this.banxicoToken } }).pipe(map(r => r.bmx.series[0]?.datos[0]?.dato));
   }

   createSpoOut(otp: string, amount: number, bankAccount: string) {
      const headers = HEADER_DONT_SAVE_REQ.set('tk', btoa(otp));

      return this.http.post<{ id: string }>(`${environment.apiUrlBase}/wallet/otp/create-spo`, { bankAccount, amount }, { headers })
         .pipe(map(res => {

         }))

   }

   /** Función para obtener la información de un proyecto en especifico */
   getInfoDev(id: string) {
      return this.http.post<{ d: any }>(`${environment.apiUrlBase}/developer/info-dev`, { id }, { headers: HEADER_DONT_SAVE_REQ });
   }

   /** Función para buscar todos los proyectos actuales del solicitante */
   getInfoAllProjects(id: string) {
      return this.http.post<{ d: any }>(`${environment.apiUrlBase}/dev/get-info-all-projects`, { id }, { headers: HEADER_DONT_SAVE_REQ });
   }

   /** Función para actualizar el monto final de la cantidad aceptada por recursos */
   updateAmountWithDraw(amount: number, id: string, nip: string) {
      return this.http.post<{ amount: number }>(`${environment.apiUrlBase}/dev/update-amount `, { amount, id, pwd: btoa(nip) }, HEADER_DONT_SAVE)
   }

   /** Función para guardar toda la información al momento de que el solicitante acepte los recursos */
   saveInfoAcceptedResource(id: string, clabeToSend: number, amountAcceptedGross: number, vatCharged: number, commissionCharged: number, amountAcceptedNet: number, info: string, actualInvestmentsMade: number) {
      return this.http.post<{ id: string }>(`${environment.apiUrlBase}/developer/save-info-resouce-accepted`, { id, clabeToSend, amountAcceptedGross, vatCharged, commissionCharged, amountAcceptedNet, info, actualInvestmentsMade });
   }

   /** Función para obtener el historico de movimientos del solicitante */
   getDevelopHistoric() {
      return forkJoin({
         develop: this.getDeveloper(),
         wallet: this.http.get<any[]>(`${environment.apiUrlBase}/developer/info-wallet`, { headers: this.cacheDeveloper.getInfoDevelopWallet || HEADER_RESET_REQ }),
         dist: this.http.get<any[]>(`${environment.apiUrlBase}/developer/info-wallet-dist`, { headers: this.cacheDeveloper.getInfoDevelopWallet || HEADER_RESET_REQ }),
      }).pipe(tap(() => { this.cacheDeveloper.getInfoDevelopWallet = {}; }))
   }

   /** Obtener saldo, información y distribución de un solicitante - No CACHE */
   getDevelopHis() {
      return forkJoin({
         develop: this.getDeveloper(true),
         wallet: this.http.get<any[]>(`${environment.apiUrlBase}/developer/info-wallet`, { headers: HEADER_RESET_REQ }),
         dist: this.http.get<any[]>(`${environment.apiUrlBase}/developer/info-wallet-dist`, { headers: HEADER_RESET_REQ }),
         liquidations: this.http.get<UserWalletLiquidationMove[]>(`${environment.apiUrlBase}/developer/info-wallet-liquidations`, { headers: HEADER_RESET_REQ }),
      })
   }

   /** =========================================
    * FUNCIONES COMENTADAS Y AGREGADAS
   ========================================== */

   /** Función para subir documentos al S3 */
   uploadDocuments(file: File,type: 'cert' | 'act' | 'ine' | 'powerIndividual' | 'brochure') {
      const data = new FormData();

      data.set(type, file);

      return this.http.post<{ url: string }>(`${environment.apiUrlBase}/developer/up/docs-step-one`, data, HEADER_DONT_SAVE).pipe(
         tap(() => this.resetDeveloperInfo = true)
      )
   }

   /** Función para actualizar los datos de la etapa 1 del registro */
   updateInfoDevStepZero(info: any, email: string) {
      return this.http.post<{ fullName: string, regStep: number }>(`${environment.apiUrlBase}/developer/rgst-dev-up-name`, { info, email }, { headers: HEADER_DONT_SAVE_REQ });
   }

   /** Función para actualizar los datos de la etapa 2 del registro del formulario */
   updateInfoDevStepOne(info: any, email: string) {
      return this.http.post<{ message: string }>(`${environment.apiUrlBase}/developer/rgst-dev-up-step-one`, { info, email }, { headers: HEADER_DONT_SAVE_REQ });
   }

   /** Función para actualizar los datos de la etapa 3 del registro del formulario */
   updateInfoDevStepTwo(info: any, email: string) {
      return this.http.post<{ message: string }>(`${environment.apiUrlBase}/developer/rgst-dev-up-step-two`, { info, email }, { headers: HEADER_DONT_SAVE_REQ });
   }

   /** Función para actualizar los datos de la etapa 3 del registro - Actualizar representante legal del formulario */
   updateInfoDevStepTwoName(info: any, email: string) {
      return this.http.post<{ message: string }>(`${environment.apiUrlBase}/developer/rgst-dev-up-step-two-user`, { info, email }, { headers: HEADER_DONT_SAVE_REQ });
   }

   /** Función para actualizar los datos de la etapa 4 del registro */
   updateInfoDevStepThree(info: any, email: string) {
      return this.http.post<{ regStep: number }>(`${environment.apiUrlBase}/developer/rgst-dev-up-step-three`, { info, email }, { headers: HEADER_DONT_SAVE_REQ });
   }

   /** Función para obtener la info de su wallet del solicintate */
   getInfoWallet() {
      return this.http.get<{ nameProject: string, image: string, wallet: any }>(`${environment.apiUrlBase}/developer/get-info-wallet`, { headers: HEADER_DONT_SAVE_REQ });
   }

   /** Función para obtener el balance disponible disponible para retirar */
   getTotalBalance() {
      return this.http.get<{ balanceTotal: number }>(`${environment.apiUrlBase}/developer/get-total-balance`, { headers: HEADER_DONT_SAVE_REQ });
   }

   /** Función para obtener el saldo total que ha retirado el solicitante */
   getTotalWithDraw() {
      return this.http.get<{ totalAmountWithDraw: number }>(`${environment.apiUrlBase}/developer/get-total-amount-withdraw`, { headers: HEADER_DONT_SAVE_REQ });
   }

   /** Función para actualizar el regStep del solicitante */
   updateRegStep(regStep: number) {
      return this.http.post<{ regStep: number }>(`${environment.apiUrlBase}/developer/update-reg-step`, { regStep }, { headers: HEADER_DONT_SAVE_REQ });
   }

   /** Función para obtener información de las participaciones por proyecto */
   getInfoParts() {
      return this.http.get<any>(`${environment.apiUrlBase}/dev/get-parts-info`, { headers: HEADER_DONT_SAVE_REQ });
   }

   /** Función para obtener la información del wallet de cada proyecto */
   getInfoWalletAllProjects() {
      return this.http.get<any>(`${environment.apiUrlBase}/dev/get-info-wallet-projects`, { headers: HEADER_DONT_SAVE_REQ });
   }

   /** Función para obtener la informacíon básica de los proyectos del solicitantes */
   getInfoProjects() {
      return this.http.get<any>(`${environment.apiUrlBase}/dev/get-info-project`, { headers: HEADER_DONT_SAVE_REQ });
   }

   /** Función para obtener los nodos de los recursos aceptados por el solicitantes */
   getResourcesAccepted(pid: string) {
      return this.http.post<any>(`${environment.apiUrlBase}/dev/get-resources-accepted`, { pid }, { headers: HEADER_DONT_SAVE_REQ });
   }

   /** Función para obtener todos los recursos aceptados por el solicitante */
   getAllResourcesAccepted(pid: string) {
      return this.http.post<{ amount: string; commission: string; iva: string; total: string; totalCommission: string; withdrawCommission: string; }>
         (`${environment.apiUrlBase}/dev/get-all-resources-accepted`, { pid }, { headers: HEADER_DONT_SAVE_REQ });
   }

   /** Aceptar recursos por el solicitante */
   acceptResource(pid: string, otp: string) {
      const headers = HEADER_DONT_SAVE_REQ.set('tk', btoa(otp));

      return this.http.post<any>(`${environment.apiUrlBase}/dev/accept-resource`, { pid }, { headers })
         .pipe(tap(() => this.resetDeveloperInfo = true));
   }

   /** Funcion para obtener el detalle de los comprobantes de recursos aceptados */
   getDetailAcceptedResources(): Observable<AcceptedFundsQueryResponse[]> {
      return this.http.get<any>(`${environment.apiUrlBase}/development/funds/accepted-funding`,
      { headers: HEADER_DONT_SAVE_REQ });
   }

   downloadAcceptedFundsCertificate(pid: string, idMove: string) {
      return this.http.get(`${environment.apiUrlBase}/development/funds/certificate/${pid}/${idMove}`, { responseType: 'arraybuffer', headers: HEADER_DONT_SAVE_REQ });
   }

   downloadStatement(year: number, month: number) {
      return this.http.get(`${environment.apiUrlBase}/user/file/statement-dev/${year}/${month}`, { responseType: 'arraybuffer', headers: HEADER_DONT_SAVE_REQ });
   }

   getSTPApplicant() {
      return this.http.get<any>(`${environment.apiUrlBase}/dev/stp-applicant`, { headers: HEADER_DONT_SAVE_REQ });
   }

   requestAcceptResources(devName: string, legalName: string, balance: number) {
      return this.http.post(`${environment.apiUrlBase}/dev/request-accept-resources`, { devName, legalName, balance }, { headers: HEADER_DONT_SAVE_REQ });
   }

}
